import { Link, useSearchParams } from "react-router";
import { getErrorMessage } from "@/lib/errors";
import { useOrdersQuery, usePrefetchOrderQuery } from "@/lib/queries";
import { cn } from "@/lib/utils";

const statuses = ["all", "pending", "confirmed", "completed"];

export function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("status") ?? "all";
  const { data, isPending, isFetching, isError, error, refetch } = useOrdersQuery(filter);
  const prefetchOrder = usePrefetchOrderQuery();
  const orders = data ?? [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-[22px] font-semibold tracking-tight">Orders</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">Manage customer orders.</p>
      </div>

      {/* Status filters */}
      <div className="flex gap-1 overflow-x-auto flex-nowrap">
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setSearchParams(s === "all" ? {} : { status: s }, { replace: true })}
            className={cn(
              "rounded-md px-3 py-1.5 text-[12px] font-medium transition-all",
              filter === s
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isError ? (
        <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-lg border">
          <p className="text-[13px] text-muted-foreground">{getErrorMessage(error)}</p>
          <button
            onClick={() => refetch()}
            className="inline-flex h-8 items-center rounded-md border px-3 text-[12px] font-medium transition-colors hover:bg-muted"
          >
            Retry
          </button>
        </div>
      ) : isPending ? (
        <div className="flex h-48 items-center justify-center rounded-lg border">
          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-lg border">
          <p className="text-[13px] text-muted-foreground">No orders found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {isFetching && (
            <p className="text-[12px] text-muted-foreground">Updating...</p>
          )}
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    #
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Customer
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Item
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Pickup
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order: any) => (
                  <tr key={order.id} className="border-b last:border-0 transition-colors hover:bg-muted/20">
                    <td className="px-4 py-3 text-[13px] font-mono text-muted-foreground">
                      {order.id}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/orders/${order.id}`}
                        onMouseEnter={() => prefetchOrder(order.id)}
                        onFocus={() => prefetchOrder(order.id)}
                        onTouchStart={() => prefetchOrder(order.id)}
                        className="text-[13px] font-medium underline-offset-4 hover:underline"
                      >
                        {order.customer_name}
                      </Link>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{order.customer_phone}</p>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-muted-foreground">
                      {order.item_name}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-muted-foreground">
                      {order.pickup_date}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="px-4 py-3 text-[13px] text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: "bg-foreground/5 text-foreground/70",
    confirmed: "bg-foreground/10 text-foreground/90",
    completed: "bg-foreground text-background",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
        styles[status] ?? styles.pending
      }`}
    >
      {status}
    </span>
  );
}
